import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface ChartSeries {
  /** Key into each data row. */
  key: string;
  label: string;
  /**
   * Any CSS color. Defaults walk the theme palette, so charts follow the
   * light/dark theme without their own color logic.
   */
  color?: string;
}

export interface SeriesBarChartProps {
  data: Array<Record<string, unknown>>;
  xKey: string;
  series: ChartSeries[];
  height?: number;
  /** Formats Y-axis ticks and tooltip values — money vs units, typically. */
  formatValue?: (value: number) => string;
}

/**
 * The categorical series palette; `var()` resolves at render, so theme switches
 * apply live and each mode gets its own steps.
 *
 * Slots are assigned in order and never cycled — the order is what keeps
 * adjacent series apart under red/green colour blindness, so reusing slot 1 for
 * a seventh series would put two indistinguishable bars side by side. Past six
 * series a chart needs folding into an "other" bucket or splitting into small
 * multiples, which is a decision for the screen, not for this component; until
 * one is made it falls back to repeating rather than rendering nothing.
 *
 * Note these are the *chart* tokens, not `--ok` / `--warn` / `--risk`: a status
 * colour carries reserved meaning and must never stand in for "series 3".
 */
const DEFAULT_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
];

/**
 * A grouped bar chart wired to the design tokens.
 *
 * The wrapper exists so no screen touches recharts directly: axes, grid,
 * tooltip and legend are styled once here against the token palette, which is
 * what keeps every chart in the suite readable in both themes instead of
 * shipping recharts' default grey-on-white.
 */
export function SeriesBarChart({
  data,
  xKey,
  series,
  height = 320,
  formatValue = (value) => new Intl.NumberFormat().format(value),
}: SeriesBarChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
        <CartesianGrid stroke="var(--line-soft)" vertical={false} />
        <XAxis
          dataKey={xKey}
          stroke="var(--ink-faint)"
          tick={{ fill: "var(--ink-muted)", fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: "var(--line)" }}
        />
        <YAxis
          stroke="var(--ink-faint)"
          tick={{ fill: "var(--ink-muted)", fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatValue}
          width={72}
        />
        <Tooltip
          cursor={{ fill: "var(--surface-sunk)" }}
          formatter={(value: number | string) =>
            typeof value === "number" ? formatValue(value) : value
          }
          contentStyle={{
            background: "var(--surface-over)",
            border: "1px solid var(--line)",
            borderRadius: 6,
            color: "var(--ink)",
            fontSize: 13,
          }}
          labelStyle={{ color: "var(--ink-muted)" }}
        />
        <Legend
          wrapperStyle={{ fontSize: 13, color: "var(--ink-muted)" }}
          iconType="circle"
          iconSize={9}
        />
        {series.map((entry, index) => (
          <Bar
            key={entry.key}
            dataKey={entry.key}
            name={entry.label}
            fill={entry.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
            radius={[3, 3, 0, 0]}
            maxBarSize={48}
            stroke="var(--surface)"
            strokeWidth={1}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
